import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

type SeekPagingCommand = 'Begin' | 'Previous' | 'Next' | 'End';

type ListSeekPaginationProps = {
    articlesCount: number;
    currentPage: number;
    pageSize?: number;
    hasPrevious: boolean;
    hasNext: boolean;
    isRefreshing?: boolean;
    onCommand: (command: SeekPagingCommand) => void | Promise<void>;
    onRefresh: () => void | Promise<void>;
};

const ListSeekPagination = (props: ListSeekPaginationProps) => {
    const { t } = useTranslation();

    if (props.articlesCount === 0) {
        return null;
    }

    const pageSize = props.pageSize ?? 25;
    const pageCount = Math.ceil(props.articlesCount / pageSize);

    const actions = [
        {
            command: 'Begin' as SeekPagingCommand,
            text: '<<',
            disabled: props.currentPage <= 1 || !!props.isRefreshing
        },
        {
            command: 'Previous' as SeekPagingCommand,
            text: '<',
            disabled: !props.hasPrevious || !!props.isRefreshing
        },
        {
            command: 'Next' as SeekPagingCommand,
            text: '>',
            disabled: !props.hasNext || !!props.isRefreshing
        },
        {
            command: 'End' as SeekPagingCommand,
            text: '>>',
            disabled: props.currentPage >= pageCount || !!props.isRefreshing
        }
    ];

    const runCommand = (command: SeekPagingCommand) => props.onCommand(command);
    const refresh = () => props.onRefresh();

    return (
        <>
            <Row>
                <Col>
                    <nav>
                        <ul className="pagination">
                            {
                                actions.map((v, idx) => {
                                    const onClick = (ev: React.MouseEvent) => {
                                        ev.preventDefault();
                                        if (!v.disabled) {
                                            runCommand(v.command);
                                        }
                                    };

                                    return (
                                        <li onClick={onClick} key={idx}>
                                            {
                                                !v.disabled
                                                    ? <a className="page-link" href="">{v.text}</a>
                                                    : <div className="page-link" style={{ color: 'gray' }}>{v.text}</div>
                                            }
                                        </li>
                                    );
                                })
                            }

                            <li
                                className="d-none d-md-block"
                                onClick={ev => {
                                    ev.preventDefault();
                                    if (!props.isRefreshing) {
                                        refresh();
                                    }
                                }}
                                key="refresh"
                            >
                                <span className="page-link">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 512 512">
                                        <title>refresh</title>
                                        <path
                                            d="M320,146s24.36-12-64-12A160,160,0,1,0,416,294"
                                            style={{ fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '32px' }}
                                        />
                                        <polyline
                                            points="256 58 336 138 256 218"
                                            style={{ fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '32px' }}
                                        />
                                    </svg>
                                </span>
                            </li>
                        </ul>
                    </nav>
                </Col>

                <Col className="text-end">
                    <div className="qvend-table-font">
                        {t('pagination.pageOf', { current: props.currentPage, total: pageCount })}
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default ListSeekPagination;